import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material';
import {
  Language,
  Notifications,
  AccountCircle,
  Email,
  Draw,
  CheckCircle,
  Lock,
} from '@mui/icons-material';
import { PageHeader } from '../../components/common/PageHeader';
import { userService, SmtpConfigUpdate } from '../../services/userService';
import { useAuthStore } from '../../stores/authStore';

export const SettingsPage = () => {
  const { t, i18n } = useTranslation();
  const { user, setAuth, accessToken, refreshToken } = useAuthStore();
  const [language, setLanguage] = useState(i18n.language || 'fr');

  // Profile state
  const [profileFirstName, setProfileFirstName] = useState(user?.first_name ?? '');
  const [profileLastName, setProfileLastName] = useState(user?.last_name ?? '');
  const [profilePhone, setProfilePhone] = useState(user?.phone ?? '');
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileAlert, setProfileAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Password state
  const [pwdCurrent, setPwdCurrent] = useState('');
  const [pwdNew, setPwdNew] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [pwdAlert, setPwdAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // SMTP state
  const [smtpConfigured, setSmtpConfigured] = useState(false);
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPassword, setSmtpPassword] = useState('');
  const [smtpFrom, setSmtpFrom] = useState('');
  const [smtpLoading, setSmtpLoading] = useState(false);
  const [smtpTesting, setSmtpTesting] = useState(false);
  const [smtpAlert, setSmtpAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Signature state
  const [signatureValue, setSignatureValue] = useState('');
  const [signatureDefault, setSignatureDefault] = useState('');
  const [signatureCustomized, setSignatureCustomized] = useState(false);
  const [signatureLoading, setSignatureLoading] = useState(false);
  const [signatureAlert, setSignatureAlert] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Load profile from API on mount (in case store data is stale)
  useEffect(() => {
    userService.getMe().then((data) => {
      setProfileFirstName(data.first_name);
      setProfileLastName(data.last_name);
      setProfilePhone(data.phone ?? '');
    }).catch(() => {});
  }, []);

  const handleProfileSave = async () => {
    setProfileLoading(true);
    setProfileAlert(null);
    try {
      await userService.updateMe({
        first_name: profileFirstName,
        last_name: profileLastName,
        phone: profilePhone || undefined,
      });
      // Sync auth store so the name is up-to-date everywhere
      if (user && accessToken && refreshToken) {
        setAuth(
          { ...user, first_name: profileFirstName, last_name: profileLastName },
          accessToken,
          refreshToken,
        );
      }
      setProfileAlert({ type: 'success', message: t('settings.profile.saveSuccess') });
    } catch {
      setProfileAlert({ type: 'error', message: t('settings.profile.saveError') });
    } finally {
      setProfileLoading(false);
    }
  };

  const handlePasswordSave = async () => {
    if (pwdNew !== pwdConfirm) {
      setPwdAlert({ type: 'error', message: t('settings.password.mismatch') });
      return;
    }
    setPwdLoading(true);
    setPwdAlert(null);
    try {
      await userService.changePassword({ current_password: pwdCurrent, new_password: pwdNew });
      setPwdCurrent('');
      setPwdNew('');
      setPwdConfirm('');
      setPwdAlert({ type: 'success', message: t('settings.password.saveSuccess') });
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ?? '';
      if (detail.toLowerCase().includes('incorrect') || detail.toLowerCase().includes('actuel')) {
        setPwdAlert({ type: 'error', message: t('settings.password.wrongCurrent') });
      } else {
        setPwdAlert({ type: 'error', message: t('settings.password.saveError') });
      }
    } finally {
      setPwdLoading(false);
    }
  };

  const handleLanguageChange = (event: SelectChangeEvent) => {
    const newLanguage = event.target.value;
    setLanguage(newLanguage);
    i18n.changeLanguage(newLanguage);
  };

  // Load signature and SMTP config on mount
  useEffect(() => {
    userService.getSignature().then((data) => {
      setSignatureDefault(data.default_signature);
      setSignatureCustomized(!!data.signature);
      setSignatureValue(data.signature ?? data.default_signature);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    userService.getSmtpConfig().then((config) => {
      setSmtpConfigured(config.is_configured);
      setSmtpHost(config.smtp_host || '');
      setSmtpPort(String(config.smtp_port || 587));
      setSmtpUser(config.smtp_user || '');
      setSmtpFrom(config.smtp_from || '');
    }).catch(() => {
      // ignore – user simply has no SMTP config yet
    });
  }, []);

  const handleSignatureSave = async () => {
    setSignatureLoading(true);
    setSignatureAlert(null);
    try {
      await userService.updateSignature(signatureValue);
      setSignatureCustomized(true);
      setSignatureAlert({ type: 'success', message: t('settings.signature.saveSuccess') });
    } catch {
      setSignatureAlert({ type: 'error', message: t('settings.signature.saveError') });
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleSignatureReset = async () => {
    if (!window.confirm(t('settings.signature.resetConfirm'))) return;
    setSignatureLoading(true);
    setSignatureAlert(null);
    try {
      await userService.deleteSignature();
      setSignatureCustomized(false);
      setSignatureValue(signatureDefault);
      setSignatureAlert({ type: 'success', message: t('settings.signature.resetSuccess') });
    } catch {
      setSignatureAlert({ type: 'error', message: t('settings.signature.saveError') });
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleSmtpSave = async () => {
    if (!smtpHost || !smtpUser || !smtpFrom) return;
    // password is required when not yet configured
    if (!smtpConfigured && !smtpPassword) return;

    setSmtpLoading(true);
    setSmtpAlert(null);
    try {
      const payload: SmtpConfigUpdate = {
        smtp_host: smtpHost,
        smtp_port: Number(smtpPort) || 587,
        smtp_user: smtpUser,
        smtp_password: smtpPassword,
        smtp_from: smtpFrom,
      };
      await userService.updateSmtpConfig(payload);
      setSmtpConfigured(true);
      setSmtpPassword('');
      setSmtpAlert({ type: 'success', message: t('settings.smtp.saveSuccess') });
    } catch {
      setSmtpAlert({ type: 'error', message: t('settings.smtp.saveError') });
    } finally {
      setSmtpLoading(false);
    }
  };

  const handleSmtpTest = async () => {
    if (!smtpPassword && !smtpConfigured) return;

    setSmtpTesting(true);
    setSmtpAlert(null);
    try {
      if (smtpPassword) {
        // Test with the password currently entered in the form
        await userService.testSmtpConfig({
          smtp_host: smtpHost,
          smtp_port: Number(smtpPort) || 587,
          smtp_user: smtpUser,
          smtp_password: smtpPassword,
          smtp_from: smtpFrom,
        });
      } else {
        // No password entered: test the already-saved configuration
        await userService.testSavedSmtpConfig();
      }
      setSmtpAlert({ type: 'success', message: t('settings.smtp.testSuccess') });
    } catch {
      setSmtpAlert({ type: 'error', message: t('settings.smtp.testError') });
    } finally {
      setSmtpTesting(false);
    }
  };

  const handleSmtpReset = async () => {
    if (!window.confirm(t('settings.smtp.resetConfirm'))) return;
    setSmtpLoading(true);
    setSmtpAlert(null);
    try {
      await userService.deleteSmtpConfig();
      setSmtpConfigured(false);
      setSmtpHost('');
      setSmtpPort('587');
      setSmtpUser('');
      setSmtpPassword('');
      setSmtpFrom('');
      setSmtpAlert({ type: 'success', message: t('settings.smtp.resetSuccess') });
    } catch {
      setSmtpAlert({ type: 'error', message: t('settings.smtp.saveError') });
    } finally {
      setSmtpLoading(false);
    }
  };

  const smtpFormValid = smtpHost && smtpUser && smtpFrom && (smtpPassword || smtpConfigured);

  return (
    <Box>
      <PageHeader
        title={t('settings.title')}
      />

      <Grid container spacing={3}>
        {/* Language Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Language color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  {t('settings.language')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {t('settings.languageDescription')}
              </Typography>
              <FormControl fullWidth>
                <InputLabel id="language-select-label">
                  {t('settings.selectLanguage')}
                </InputLabel>
                <Select
                  labelId="language-select-label"
                  id="language-select"
                  value={language}
                  label={t('settings.selectLanguage')}
                  onChange={handleLanguageChange}
                >
                  <MenuItem value="fr">{t('settings.french')}</MenuItem>
                  <MenuItem value="en">{t('settings.english')}</MenuItem>
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>

        {/* Email Signature */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Draw color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  {t('settings.signature.title')}
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  {signatureCustomized ? (
                    <Chip icon={<CheckCircle />} label={t('settings.signature.customized')} color="success" size="small" />
                  ) : (
                    <Chip label={t('settings.signature.default')} size="small" variant="outlined" />
                  )}
                </Box>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('settings.signature.description')}
              </Typography>

              {signatureAlert && (
                <Alert severity={signatureAlert.type} sx={{ mb: 2 }} onClose={() => setSignatureAlert(null)}>
                  {signatureAlert.message}
                </Alert>
              )}

              <TextField
                label={t('settings.signature.label')}
                value={signatureValue}
                onChange={(e) => setSignatureValue(e.target.value)}
                multiline
                minRows={3}
                fullWidth
                size="small"
                sx={{ mb: 2, fontFamily: 'monospace' }}
              />

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  onClick={handleSignatureSave}
                  disabled={signatureLoading}
                  startIcon={signatureLoading ? <CircularProgress size={16} /> : undefined}
                >
                  {t('settings.signature.save')}
                </Button>
                {signatureCustomized && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={handleSignatureReset}
                    disabled={signatureLoading}
                    sx={{ ml: 'auto' }}
                  >
                    {t('settings.signature.reset')}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* SMTP Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Email color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  {t('settings.smtp.title')}
                </Typography>
                {smtpConfigured && (
                  <Box sx={{ ml: 'auto' }}>
                    <Chip
                      icon={<CheckCircle />}
                      label={t('settings.smtp.configured')}
                      color="success"
                      size="small"
                    />
                  </Box>
                )}
              </Box>
              {!smtpConfigured && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
                  {t('settings.smtp.notConfigured')}
                </Typography>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('settings.smtp.description')}
              </Typography>

              {smtpAlert && (
                <Alert severity={smtpAlert.type} sx={{ mb: 2 }} onClose={() => setSmtpAlert(null)}>
                  {smtpAlert.message}
                </Alert>
              )}

              <TextField
                label={t('settings.smtp.host')}
                value={smtpHost}
                onChange={(e) => setSmtpHost(e.target.value)}
                placeholder={t('settings.smtp.hostPlaceholder')}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <TextField
                  label={t('settings.smtp.port')}
                  value={smtpPort}
                  onChange={(e) => setSmtpPort(e.target.value)}
                  size="small"
                  sx={{ width: 110 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('settings.smtp.portHint')}
                </Typography>
              </Box>

              <TextField
                label={t('settings.smtp.user')}
                value={smtpUser}
                onChange={(e) => setSmtpUser(e.target.value)}
                placeholder={t('settings.smtp.userPlaceholder')}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              />

              <TextField
                label={t('settings.smtp.password')}
                type="password"
                value={smtpPassword}
                onChange={(e) => setSmtpPassword(e.target.value)}
                placeholder={smtpConfigured ? t('settings.smtp.passwordPlaceholder') : ''}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              />

              <TextField
                label={t('settings.smtp.from')}
                value={smtpFrom}
                onChange={(e) => setSmtpFrom(e.target.value)}
                placeholder={t('settings.smtp.fromPlaceholder')}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  onClick={handleSmtpSave}
                  disabled={!smtpFormValid || smtpLoading}
                  startIcon={smtpLoading ? <CircularProgress size={16} /> : undefined}
                >
                  {t('settings.smtp.save')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleSmtpTest}
                  disabled={!smtpFormValid || smtpTesting}
                  startIcon={smtpTesting ? <CircularProgress size={16} /> : undefined}
                >
                  {t('settings.smtp.test')}
                </Button>
                {smtpConfigured && (
                  <Button
                    variant="text"
                    color="error"
                    onClick={handleSmtpReset}
                    disabled={smtpLoading}
                    sx={{ ml: 'auto' }}
                  >
                    {t('settings.smtp.reset')}
                  </Button>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Profile */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <AccountCircle color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  {t('settings.profile.title')}
                </Typography>
              </Box>

              {profileAlert && (
                <Alert severity={profileAlert.type} sx={{ mb: 2 }} onClose={() => setProfileAlert(null)}>
                  {profileAlert.message}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  label={t('settings.profile.firstName')}
                  value={profileFirstName}
                  onChange={(e) => setProfileFirstName(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label={t('settings.profile.lastName')}
                  value={profileLastName}
                  onChange={(e) => setProfileLastName(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Box>

              <TextField
                label={t('settings.profile.email')}
                value={user?.email ?? ''}
                size="small"
                fullWidth
                disabled
                sx={{ mb: 2 }}
              />

              <TextField
                label={t('settings.profile.phone')}
                value={profilePhone}
                onChange={(e) => setProfilePhone(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                onClick={handleProfileSave}
                disabled={profileLoading || !profileFirstName || !profileLastName}
                startIcon={profileLoading ? <CircularProgress size={16} /> : undefined}
              >
                {t('settings.profile.save')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Security – Change password */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Lock color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  {t('settings.password.title')}
                </Typography>
              </Box>

              {pwdAlert && (
                <Alert severity={pwdAlert.type} sx={{ mb: 2 }} onClose={() => setPwdAlert(null)}>
                  {pwdAlert.message}
                </Alert>
              )}

              <TextField
                label={t('settings.password.current')}
                type="password"
                value={pwdCurrent}
                onChange={(e) => setPwdCurrent(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
              />

              <Divider sx={{ mb: 2 }} />

              <TextField
                label={t('settings.password.new')}
                type="password"
                value={pwdNew}
                onChange={(e) => setPwdNew(e.target.value)}
                size="small"
                fullWidth
                sx={{ mb: 2 }}
                helperText={t('settings.password.requirements')}
              />

              <TextField
                label={t('settings.password.confirm')}
                type="password"
                value={pwdConfirm}
                onChange={(e) => setPwdConfirm(e.target.value)}
                size="small"
                fullWidth
                error={!!pwdConfirm && pwdNew !== pwdConfirm}
                sx={{ mb: 2 }}
              />

              <Button
                variant="contained"
                onClick={handlePasswordSave}
                disabled={pwdLoading || !pwdCurrent || !pwdNew || !pwdConfirm}
                startIcon={pwdLoading ? <CircularProgress size={16} /> : undefined}
              >
                {t('settings.password.save')}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Notifications placeholder */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Notifications color="primary" />
                <Typography variant="h6" fontWeight={700}>
                  {t('settings.notifications')}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t('settings.comingSoon')}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};
